import { useOpenSCAD } from '@/hooks/useOpenSCAD';
import { useProjectionViews } from '@/hooks/useProjectionViews';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { ThreeScene } from '@/components/viewer/ThreeScene';
import { DrawingSheet } from '@/components/viewer/drawing/DrawingSheet';
import { DrawingJobControls } from '@/components/viewer/drawing/DrawingJobControls';
import { SurfaceImageControls } from '@/components/viewer/SurfaceImageControls';
import type { TopSurfaceBounds } from '@/components/viewer/SurfaceImageLayer';
import { STLLoader } from 'three/addons/loaders/STLLoader.js';
import { BufferGeometry } from 'three';
import { Loader2, CircleAlert, Wrench, Box, FileText } from 'lucide-react';
import { ViewerFeedback } from '@/components/viewer/ViewerFeedback';
import { Button } from '@/components/ui/button';
import OpenSCADError from '@/lib/OpenSCADError';
import { cn } from '@/lib/utils';
import { useConversation } from '@/services/conversationService';
import { useCurrentMessage } from '@/contexts/CurrentMessageContext';
import { Content, Parameter } from '@shared/types';
import type { DesignSpec, ImageAsset, SurfaceImage } from '@shared/spec';
import {
  useSendContentMutation,
  useChangeParameters,
  useUpdateMessageOptimisticMutation,
} from '@/services/messageService';
import { useBlob } from '@/contexts/BlobContext';
import { useMeshFiles } from '@/contexts/MeshFilesContext';
import { validateParameterValue } from '@/utils/parameterUtils';
import {
  getConversationArtifactPath,
  getConversationSurfaceImagePath,
} from '@/lib/firebaseAssetPaths';
import { storage } from '@/lib/firebase';
import { ref, uploadBytes } from 'firebase/storage';
import {
  downloadConversationAssetBlob,
  getPreviewMeshFile,
} from '@/lib/conversationAssets';
import { useTranslation } from 'react-i18next';
import { toPlainMessage } from '@/utils/plainMessage';

// Extract import() filenames from OpenSCAD code
function extractImportFilenames(code: string): string[] {
  const importRegex = /import\s*\(\s*"([^"]+)"\s*\)/g;
  const filenames: string[] = [];
  let match;
  while ((match = importRegex.exec(code)) !== null) {
    filenames.push(match[1]);
  }
  return filenames;
}

const OPENSCAD_COMPILE_DEBOUNCE_MS = 350;

export function OpenSCADViewer() {
  const { t } = useTranslation('common');
  const { conversation } = useConversation();
  const { currentMessage, setCurrentMessage } = useCurrentMessage();
  const { setBlob } = useBlob();
  const { compileScad, clearOutput, writeFile, isCompiling, output, isError, error } =
    useOpenSCAD();
  const { getMeshFile, hasMeshFile } = useMeshFiles();
  const [geometry, setGeometry] = useState<BufferGeometry | null>(null);
  const [storedArtifactBlob, setStoredArtifactBlob] = useState<Blob | null>(
    null,
  );
  const [compiledParameters, setCompiledParameters] = useState<Parameter[] | null>(
    null,
  );
  const { mutate: sendMessage } = useSendContentMutation({ conversation });
  const { mutate: updateMessageOptimistic } = useUpdateMessageOptimisticMutation();
  const [activeTab, setActiveTab] = useState<'3d' | '2d'>('3d');
  const [backendDrawingUrl, setBackendDrawingUrl] = useState<string | null>(
    null,
  );
  const [visualSpec, setVisualSpec] = useState<DesignSpec | null>(null);
  const visualSpecRef = useRef<DesignSpec | null>(null);
  const persistVisualSpecTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [surfaceImageUrls, setSurfaceImageUrls] = useState<Record<string, string>>({});
  const [selectedSurfaceImageId, setSelectedSurfaceImageId] = useState<string | null>(null);
  const [isUploadingSurfaceImage, setIsUploadingSurfaceImage] = useState(false);
  const {
    projections,
    sections,
    componentProjections,
    compileProjections,
    compileSections,
    compileComponentViews,
    writeFile: writeProjectionFile,
    invalidateCache,
  } = useProjectionViews();
  // Track which files (and their versions) we've written to avoid re-writing
  // Maps filename -> Blob instance
  const writtenFilesRef = useRef<Map<string, Blob>>(new Map());
  // Track which files have been written to the projection worker
  const projectionWrittenFilesRef = useRef<Map<string, Blob>>(new Map());
  const artifactUploadInFlightRef = useRef<string | null>(null);
  const compileDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSubmittedScadRef = useRef<string | null>(null);

  const changeParameters = useChangeParameters();
  const parameters = useMemo(
    () => currentMessage?.content.artifact?.parameters ?? [],
    [currentMessage?.content.artifact?.parameters],
  );
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingParametersRef = useRef<Parameter[] | null>(null);
  const parametersRef = useRef<Parameter[]>(parameters);

  useEffect(() => {
    parametersRef.current = parameters;
  }, [parameters]);

  const debouncedCommit = useCallback(
    (params: Parameter[]) => {
      pendingParametersRef.current = params;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        if (pendingParametersRef.current && currentMessage) {
          changeParameters(
            toPlainMessage(currentMessage),
            pendingParametersRef.current,
          );
          pendingParametersRef.current = null;
        }
      }, 200);
    },
    [changeParameters, currentMessage],
  );

  const handleParameterChange = useCallback(
    (_param: Parameter, _value: number) => {
      // Live preview during drag — no-op for now (label shows local value)
    },
    [],
  );

  const handleParameterCommit = useCallback(
    (param: Parameter, value: number) => {
      const validatedValue = validateParameterValue(param, value);
      const updatedParam = { ...param, value: validatedValue };
      const updatedParameters = parameters.map((p) =>
        p.name === param.name ? updatedParam : p,
      );
      debouncedCommit(updatedParameters);
    },
    [parameters, debouncedCommit],
  );

  const scadCode = currentMessage?.content.artifact?.code;
  const artifactSpec = currentMessage?.content.artifact?.spec;
  const drawingMetadata = currentMessage?.content.artifact?.drawingMetadata;
  const effectiveSpec = visualSpec ?? artifactSpec ?? undefined;
  const hasRenderableParts = (artifactSpec?.parts?.length ?? 0) > 0;
  const previewFile = getPreviewMeshFile(currentMessage?.content.artifact);
  const artifactStoragePath = previewFile?.storagePath;
  const effectiveOutput = output ?? storedArtifactBlob ?? undefined;
  const hasPreviewGeometry = !!geometry;
  const liveMeshScale = useMemo(
    () => parameterPreviewScale(compiledParameters, parameters),
    [compiledParameters, parameters],
  );
  const topSurfaceBounds = useMemo(
    () => (geometry ? getTopSurfaceBounds(geometry) : null),
    [geometry],
  );

  useEffect(() => {
    const next = artifactSpec ?? null;
    setVisualSpec(next);
    visualSpecRef.current = next;
    setSelectedSurfaceImageId((id) =>
      id && next?.surfaceImages?.some((image) => image.id === id)
        ? id
        : (next?.surfaceImages?.[0]?.id ?? null),
    );
  }, [currentMessage?.id, artifactSpec]);

  const persistVisualSpec = useCallback(
    (nextSpec: DesignSpec, immediate: boolean) => {
      if (!currentMessage?.content.artifact) return;
      const baseMessage = toPlainMessage(currentMessage);
      const artifact = baseMessage.content.artifact;
      if (!artifact) return;
      const nextMessage = {
        ...baseMessage,
        content: {
          ...baseMessage.content,
          artifact: {
            ...artifact,
            spec: nextSpec,
          },
        },
      };

      setCurrentMessage(nextMessage);

      if (persistVisualSpecTimerRef.current) {
        clearTimeout(persistVisualSpecTimerRef.current);
        persistVisualSpecTimerRef.current = null;
      }

      const write = () => updateMessageOptimistic({ message: nextMessage });
      if (immediate) {
        write();
      } else {
        persistVisualSpecTimerRef.current = setTimeout(write, 300);
      }
    },
    [currentMessage, setCurrentMessage, updateMessageOptimistic],
  );

  useEffect(() => {
    return () => {
      if (persistVisualSpecTimerRef.current) {
        clearTimeout(persistVisualSpecTimerRef.current);
      }
    };
  }, []);

  const updateVisualSpec = useCallback(
    (mutator: (spec: DesignSpec) => DesignSpec, immediate: boolean) => {
      const base = visualSpecRef.current ?? artifactSpec;
      if (!base) return;
      const next = mutator(base);
      visualSpecRef.current = next;
      setVisualSpec(next);
      persistVisualSpec(next, immediate);
    },
    [artifactSpec, persistVisualSpec],
  );

  const imageAssetKey = useMemo(
    () =>
      (effectiveSpec?.imageAssets ?? [])
        .map((asset) => `${asset.id}:${asset.storagePath}`)
        .join('|'),
    [effectiveSpec?.imageAssets],
  );

  useEffect(() => {
    const assets = effectiveSpec?.imageAssets ?? [];
    if (assets.length === 0) {
      setSurfaceImageUrls({});
      return;
    }

    let cancelled = false;
    const objectUrls: string[] = [];
    Promise.all(
      assets.map(async (asset) => {
        try {
          const blob = await downloadConversationAssetBlob(
            conversation.id,
            asset.storagePath,
          );
          const url = URL.createObjectURL(blob);
          objectUrls.push(url);
          return [asset.id, url] as const;
        } catch (error) {
          console.warn('[SurfaceImages] failed to resolve image URL:', error);
          return [asset.id, ''] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      setSurfaceImageUrls(
        Object.fromEntries(entries.filter(([, url]) => !!url)),
      );
    });

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [conversation.id, imageAssetKey, effectiveSpec?.imageAssets]);

  useEffect(() => {
    let cancelled = false;

    const loadStoredArtifact = async () => {
      if (!artifactStoragePath) {
        setStoredArtifactBlob(null);
        return;
      }

      try {
        const blob = await downloadConversationAssetBlob(
          conversation.id,
          artifactStoragePath,
        );
        if (!cancelled) {
          setStoredArtifactBlob(blob);
        }
      } catch (error) {
        console.error('[OpenSCAD] Failed to restore stored artifact:', error);
        if (!cancelled) {
          setStoredArtifactBlob(null);
        }
      }
    };

    loadStoredArtifact();

    return () => {
      cancelled = true;
    };
  }, [artifactStoragePath, conversation.id]);

  useEffect(() => {
    if (compileDebounceRef.current) {
      clearTimeout(compileDebounceRef.current);
      compileDebounceRef.current = null;
    }

    if (!scadCode || !hasRenderableParts) {
      lastSubmittedScadRef.current = null;
      clearOutput();
      setStoredArtifactBlob(null);
      setCompiledParameters(null);
      return;
    }
    if (scadCode === lastSubmittedScadRef.current || isCompiling) {
      return;
    }

    let cancelled = false;

    const compileWithMeshFiles = async () => {
      try {
        if (cancelled || scadCode === lastSubmittedScadRef.current) return;
        // Extract any import() filenames from the code
        const importedFiles = extractImportFilenames(scadCode);

        // Write any mesh files that haven't been written yet
        for (const filename of importedFiles) {
          const inContext = hasMeshFile(filename);
          const meshContent = getMeshFile(filename);

          // Check if we need to write:
          // 1. File exists in context
          // 2. We haven't written it OR the content has changed (new Blob reference)
          const writtenBlob = writtenFilesRef.current.get(filename);
          const needsWrite =
            inContext &&
            meshContent &&
            (!writtenBlob || writtenBlob !== meshContent);

          if (needsWrite && meshContent) {
            await writeFile(filename, meshContent);
            writtenFilesRef.current.set(filename, meshContent);
          }
        }

        // Now compile the code
        lastSubmittedScadRef.current = scadCode;
        compileScad(scadCode);
      } catch (err) {
        console.error('[OpenSCAD] Error preparing files for compilation:', err);
      }
    };

    compileDebounceRef.current = setTimeout(
      compileWithMeshFiles,
      OPENSCAD_COMPILE_DEBOUNCE_MS,
    );

    return () => {
      cancelled = true;
      if (compileDebounceRef.current) {
        clearTimeout(compileDebounceRef.current);
        compileDebounceRef.current = null;
      }
    };
  }, [
    scadCode,
    hasRenderableParts,
    isCompiling,
    clearOutput,
    compileScad,
    writeFile,
    getMeshFile,
    hasMeshFile,
  ]);

  useEffect(() => {
    setBlob(effectiveOutput ?? null);
    if (effectiveOutput && effectiveOutput instanceof Blob) {
      effectiveOutput.arrayBuffer().then((buffer) => {
        const loader = new STLLoader();
        const geom = loader.parse(buffer);
        geom.center();
        geom.computeVertexNormals();
        setGeometry(geom);
        setCompiledParameters(parametersRef.current);
      });
    } else {
      setGeometry(null);
      setCompiledParameters(null);
    }
  }, [effectiveOutput, setBlob]);

  const handleSurfaceImageChange = useCallback(
    (image: SurfaceImage, commit: boolean) => {
      updateVisualSpec((spec) => {
        const surfaceImages = spec.surfaceImages ?? [];
        return {
          ...spec,
          surfaceImages: surfaceImages.map((candidate) =>
            candidate.id === image.id ? image : candidate,
          ),
        };
      }, commit);
    },
    [updateVisualSpec],
  );

  const handleSurfaceImageRemove = useCallback(
    (id: string) => {
      updateVisualSpec((spec) => {
        const removed = spec.surfaceImages?.find((image) => image.id === id);
        const surfaceImages = (spec.surfaceImages ?? []).filter(
          (image) => image.id !== id,
        );
        const imageAssets =
          removed &&
          !surfaceImages.some((image) => image.assetId === removed.assetId)
            ? (spec.imageAssets ?? []).filter(
                (asset) => asset.id !== removed.assetId,
              )
            : (spec.imageAssets ?? []);
        return { ...spec, imageAssets, surfaceImages };
      }, true);
      setSelectedSurfaceImageId(null);
    },
    [updateVisualSpec],
  );

  const handleSurfaceImageUpload = useCallback(
    async (file: File) => {
      const baseSpec = visualSpecRef.current ?? artifactSpec;
      if (!baseSpec || !conversation.user_id || !conversation.id || !topSurfaceBounds) {
        return;
      }

      setIsUploadingSurfaceImage(true);
      try {
        const dimensions = await readImageDimensions(file);
        const imageId = crypto.randomUUID();
        const extension = imageExtensionForFile(file);
        const storagePath = getConversationSurfaceImagePath(
          conversation.user_id,
          conversation.id,
          imageId,
          extension,
        );

        await uploadBytes(ref(storage, storagePath), file, {
          contentType: file.type || 'image/png',
        });

        const asset: ImageAsset = {
          id: imageId,
          storagePath,
          contentType: file.type || 'image/png',
          width: dimensions.width,
          height: dimensions.height,
          fileName: file.name,
          kind: 'decal',
          createdAt: new Date().toISOString(),
        };
        const surfaceImage: SurfaceImage = {
          id: `surface-${imageId}`,
          assetId: imageId,
          target: { kind: 'object_top' },
          mode: 'decal',
          center: [0, 0],
          size: fitImageToTopSurface(dimensions, topSurfaceBounds),
          rotationDeg: 0,
          opacity: 1,
          renderOrder: baseSpec.surfaceImages?.length ?? 0,
          locked: false,
        };

        updateVisualSpec((spec) => ({
          ...spec,
          imageAssets: [...(spec.imageAssets ?? []), asset],
          surfaceImages: [...(spec.surfaceImages ?? []), surfaceImage],
        }), true);
        setSelectedSurfaceImageId(surfaceImage.id);
      } catch (error) {
        console.error('[SurfaceImages] upload failed:', error);
      } finally {
        setIsUploadingSurfaceImage(false);
      }
    },
    [artifactSpec, conversation.id, conversation.user_id, topSurfaceBounds, updateVisualSpec],
  );

  useEffect(() => {
    const artifact = currentMessage?.content.artifact;
    if (
      !output ||
      !(output instanceof Blob) ||
      !currentMessage ||
      currentMessage.role !== 'assistant' ||
      !artifact ||
      // Already persisted: any preview-role STL file is present.
      getPreviewMeshFile(artifact) ||
      !conversation.user_id ||
      !conversation.id
    ) {
      return;
    }

    if (artifactUploadInFlightRef.current === currentMessage.id) {
      return;
    }

    const storagePath = getConversationArtifactPath(
      conversation.user_id,
      conversation.id,
      currentMessage.id,
      'stl',
    );

    let cancelled = false;
    artifactUploadInFlightRef.current = currentMessage.id;

    const persistArtifact = async () => {
      try {
        await uploadBytes(ref(storage, storagePath), output, {
          contentType: 'model/stl',
        });

        if (cancelled) {
          return;
        }

        const previewArtifactFile = {
          format: 'stl' as const,
          storagePath,
          contentType: 'model/stl',
          role: 'preview' as const,
          byteLength: output.size,
          generatedAt: new Date().toISOString(),
        };
        const existingFiles = artifact.files ?? [];
        const nextFiles = [
          ...existingFiles.filter(
            (f) => !(f.role === 'preview' && f.format === 'stl'),
          ),
          previewArtifactFile,
        ];
        const baseMessage = toPlainMessage(currentMessage);

        updateMessageOptimistic({
          message: {
            ...baseMessage,
            content: {
              ...baseMessage.content,
              artifact: {
                ...artifact,
                files: nextFiles,
              },
            },
          },
        });
      } catch (error) {
        console.error('[OpenSCAD] Failed to persist compiled artifact:', error);
      } finally {
        if (artifactUploadInFlightRef.current === currentMessage.id) {
          artifactUploadInFlightRef.current = null;
        }
      }
    };

    persistArtifact();

    return () => {
      cancelled = true;
    };
  }, [
    output,
    currentMessage,
    conversation.user_id,
    conversation.id,
    updateMessageOptimistic,
  ]);

  const fixError = useCallback(
    async (error: OpenSCADError) => {
      const newContent: Content = {
        text: t('viewer.fixWithAI'),
        error: error.stdErr.join('\n'),
      };

      sendMessage(newContent);
    },
    [sendMessage, t],
  );

  const isLastMessage =
    conversation.current_message_leaf_id === currentMessage?.id;

  // Write mesh files to the projection worker and trigger compilation
  const triggerProjectionCompile = useCallback(
    async (code: string) => {
      const importedFiles = extractImportFilenames(code);
      for (const filename of importedFiles) {
        const inContext = hasMeshFile(filename);
        const meshContent = getMeshFile(filename);
        const writtenBlob = projectionWrittenFilesRef.current.get(filename);
        const needsWrite =
          inContext &&
          meshContent &&
          (!writtenBlob || writtenBlob !== meshContent);
        if (needsWrite && meshContent) {
          await writeProjectionFile(filename, meshContent);
          projectionWrittenFilesRef.current.set(filename, meshContent);
        }
      }
      compileProjections(code);
    },
    [compileProjections, writeProjectionFile, getMeshFile, hasMeshFile],
  );

  const handleTabChange = useCallback(
    (tab: '3d' | '2d') => {
      setActiveTab(tab);
      if (tab === '2d' && scadCode && hasRenderableParts) {
        triggerProjectionCompile(scadCode);
        const sectionPlanes = drawingMetadata?.sectionPlanes;
        if (sectionPlanes && sectionPlanes.length > 0) {
          compileSections(scadCode, sectionPlanes);
        }
        if (drawingMetadata?.layoutMode === 'components' && drawingMetadata.componentDrawings?.length) {
          compileComponentViews(
            scadCode,
            drawingMetadata.componentDrawings.map(c => ({
              componentId: c.componentId,
              views: c.views as Array<'top' | 'front' | 'right' | 'left' | 'rear' | 'bottom'>,
            })),
          );
        }
      }
    },
    [scadCode, hasRenderableParts, triggerProjectionCompile, drawingMetadata, compileSections, compileComponentViews],
  );

  // Recompile projections when code changes while on 2D tab
  useEffect(() => {
    if (activeTab === '2d' && scadCode && hasRenderableParts) {
      invalidateCache();
      triggerProjectionCompile(scadCode);
    }
  }, [scadCode, hasRenderableParts]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasContent = geometry || (isError && !storedArtifactBlob);

  return (
    <div className="relative h-full w-full bg-k-canvas transition-colors duration-200">
      {hasContent && (
        <>
          {/* Tab bar */}
          <div className="absolute left-1/2 top-3 z-10 -translate-x-1/2">
            <div className="inline-flex h-10 items-center justify-center rounded-xl border-2 border-brand-indigo/15 bg-k-card p-1">
              <button
                onClick={() => handleTabChange('3d')}
                className={cn(
                  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-bold transition-colors',
                  activeTab === '3d'
                    ? 'bg-brand-indigo text-brand-cream'
                    : 'text-brand-indigo hover:bg-brand-indigo/10',
                )}
              >
                <Box className="h-4 w-4" />
                {t('viewer.view3D')}
              </button>
              <button
                onClick={() => handleTabChange('2d')}
                className={cn(
                  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-bold transition-colors',
                  activeTab === '2d'
                    ? 'bg-brand-indigo text-brand-cream'
                    : 'text-brand-indigo hover:bg-brand-indigo/10',
                )}
              >
                <FileText className="h-4 w-4" />
                {t('viewer.view2D')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* 3D View — always mounted so WebGL context is never lost */}
      <div className={cn('h-full w-full', hasContent && activeTab !== '3d' && 'invisible')}>
        {geometry ? (
          <>
            <ThreeScene
              geometry={geometry}
              viewHints={effectiveSpec?.viewHints}
              spec={effectiveSpec}
              surfaceImageUrls={surfaceImageUrls}
              selectedSurfaceImageId={selectedSurfaceImageId}
              onSurfaceImageSelect={setSelectedSurfaceImageId}
              onSurfaceImageChange={handleSurfaceImageChange}
              meshScale={liveMeshScale}
            />
            {activeTab === '3d' && effectiveSpec && (
              <SurfaceImageControls
                spec={effectiveSpec}
                selectedId={selectedSurfaceImageId}
                topBounds={topSurfaceBounds}
                uploading={isUploadingSurfaceImage}
                onUpload={handleSurfaceImageUpload}
                onSelect={setSelectedSurfaceImageId}
                onChange={handleSurfaceImageChange}
                onRemove={handleSurfaceImageRemove}
              />
            )}
          </>
        ) : isError && !storedArtifactBlob ? (
          <div className="flex h-full items-center justify-center">
            <FixWithAIButton
              error={error}
              fixError={isLastMessage ? fixError : undefined}
            />
          </div>
        ) : null}
      </div>

      {/* 2D View — overlaid on top when active */}
      {hasContent && activeTab === '2d' && (
        <div className="absolute inset-0 z-[5] flex flex-col">
          {currentMessage?.content.artifact && conversation?.id && (
            <div className="px-3 pt-3">
              <DrawingJobControls
                conversationId={conversation.id}
                messageId={currentMessage.id}
                artifact={currentMessage.content.artifact}
                drawingPrefs={conversation.intake?.drawing}
                autoGenerate={activeTab === '2d'}
                onPreviewUrlChange={setBackendDrawingUrl}
              />
            </div>
          )}
          <div className="relative flex-1">
            {backendDrawingUrl ? (
              <BackendDrawingPreview url={backendDrawingUrl} />
            ) : (
              <DrawingSheet
                projections={projections}
                componentProjections={componentProjections}
                sections={sections}
                parameters={parameters}
                objectName={currentMessage?.content.artifact?.title}
                metadata={currentMessage?.content.artifact?.drawingMetadata}
                onParameterChange={handleParameterChange}
                onParameterCommit={handleParameterCommit}
              />
            )}
          </div>
        </div>
      )}

      {/* Feedback input for iterating on 3D/2D */}
      {hasContent && isLastMessage && !isCompiling && (
        <ViewerFeedback activeTab={activeTab} />
      )}

      {/* Loading overlay for the first 3D compile. Once a mesh exists, keep it visible. */}
      {isCompiling && !hasPreviewGeometry && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-k-canvas/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 px-6 py-4">
            <Loader2 className="h-6 w-6 animate-spin text-brand-indigo" />
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-indigo/70">
              {t('viewer.compiling')}
            </p>
          </div>
        </div>
      )}
      {isCompiling && hasPreviewGeometry && (
        <div className="pointer-events-none absolute right-4 top-16 z-20 flex items-center gap-2 rounded-md border border-brand-indigo/15 bg-k-card/90 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-indigo shadow-sm">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t('viewer.compiling')}
        </div>
      )}

    </div>
  );
}

function getTopSurfaceBounds(geometry: BufferGeometry): TopSurfaceBounds | null {
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) return null;
  return {
    minX: box.min.x,
    maxX: box.max.x,
    minY: box.min.y,
    maxY: box.max.y,
    topZ: box.max.z,
    width: box.max.x - box.min.x,
    depth: box.max.y - box.min.y,
  };
}

function parameterPreviewScale(
  compiled: Parameter[] | null,
  current: Parameter[],
): [number, number, number] {
  if (!compiled || compiled.length === 0 || current.length === 0) {
    return [1, 1, 1];
  }

  const sx = ratioForParameter(
    compiled,
    current,
    [
      'body_width',
      'overall_width',
      'model_width',
      'width',
      'core_width',
      'arm_outset',
    ],
  );
  const sy = ratioForParameter(compiled, current, [
    'body_depth',
    'overall_depth',
    'model_depth',
    'depth',
  ]);
  const sz = ratioForParameter(
    compiled,
    current,
    [
      'body_height',
      'overall_height',
      'model_height',
      'height',
      'dome_height',
      'arm_drop',
    ],
  );

  return [sx, sy, sz];
}

function ratioForParameter(
  compiled: Parameter[],
  current: Parameter[],
  names: string[],
): number {
  for (const name of names) {
    const before = numericParameterValue(compiled, name);
    const after = numericParameterValue(current, name);
    if (before != null && after != null && Math.abs(before) > 0.0001) {
      return clampScale(after / before);
    }
  }
  return 1;
}

function numericParameterValue(
  parameters: Parameter[],
  name: string,
): number | null {
  const found = parameters.find((param) => param.name === name);
  if (!found) return null;
  const value = Number(found.value);
  return Number.isFinite(value) ? value : null;
}

function clampScale(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(5, Math.max(0.2, value));
}

function fitImageToTopSurface(
  image: { width: number; height: number },
  bounds: TopSurfaceBounds,
): [number, number] {
  const aspect = image.height / image.width || 1;
  const maxWidth = Math.max(8, bounds.width * 0.55);
  const maxHeight = Math.max(8, bounds.depth * 0.55);
  let width = Math.min(maxWidth, 160);
  let height = width * aspect;
  if (height > maxHeight) {
    height = maxHeight;
    width = height / aspect;
  }
  return [Math.max(5, width), Math.max(5, height)];
}

function imageExtensionForFile(file: File): string {
  const byType: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
  };
  if (byType[file.type]) return byType[file.type];
  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension && /^[a-z0-9]{2,5}$/.test(extension) ? extension : 'png';
}

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth || 1, height: image.naturalHeight || 1 });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image_dimensions_failed'));
    };
    image.src = url;
  });
}

function BackendDrawingPreview({ url }: { url: string }) {
  const { t } = useTranslation('common');
  return (
    <div className="h-full w-full bg-white p-2">
      <iframe
        title={t('drawing.manufacturingDrawing')}
        src={url}
        className="h-full w-full rounded-sm border border-synergy-neutral-300 bg-white"
      />
    </div>
  );
}

function FixWithAIButton({
  error,
  fixError,
}: {
  error?: OpenSCADError | Error;
  fixError?: (error: OpenSCADError) => void;
}) {
  const { t } = useTranslation('common');
  const isScadErr = !!error && error.name === 'OpenSCADError';
  // OpenSCAD WASM emits compile diagnostics to stderr. Show them so the user
  // (or a Claude Code session) can copy the line numbers and parser messages
  // directly — without this block the only path to the text was via the chat
  // payload, which UserMessage hides behind the Fix-With-AI wrench.
  const stdErr = isScadErr ? (error as OpenSCADError).stdErr : [];
  const stdErrText = stdErr.join('\n').trim();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-5 p-6">
      <div className="flex flex-col items-center gap-3">
        <CircleAlert className="h-7 w-7 text-brand-indigo" strokeWidth={2.25} />
        <div className="text-center">
          <p className="font-display text-base font-bold text-brand-indigo">
            {t('viewer.compileError')}
          </p>
          <p className="mt-1 text-sm text-brand-indigo/70">
            {t('viewer.compileErrorDesc')}
          </p>
        </div>
      </div>
      {stdErrText && (
        <pre className="max-h-48 w-full max-w-2xl overflow-auto rounded-sm border border-brand-indigo/20 bg-synergy-neutral-900 p-3 text-left font-mono text-[11px] leading-snug text-synergy-text-primary">
          {stdErrText}
        </pre>
      )}
      {fixError && isScadErr && (
        <Button
          variant="secondary"
          className="group flex items-center gap-2"
          onClick={() => fixError(error as OpenSCADError)}
        >
          <Wrench className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
          <span>{t('viewer.fixWithAI')}</span>
        </Button>
      )}
    </div>
  );
}
